import ufeApi from 'services/api/ufeApi';

// Get State & City for Zip Code API
// https://jira.sephora.com/wiki/pages/viewpage.action?pageId=120042048

// AVS Loqate v2 (GIM-5453): the BFF exposes lookupCitiesByPostalCode which can return a LIST of
// cities for a single ZIP/postal code (a ZIP may map to several cities). The shopper then must
// pick one (multi-city dropdown), while a single-city ZIP auto-fills + locks city/state. The
// legacy endpoint (flag off) returns a single { city, state } shape and is left untouched.
//
// GIM-5588: lookupCitiesByPostalCode also accepts OPTIONAL address details (address1/address2/
// state). When the shopper has already entered street/state, forwarding them lets the service
// narrow the returned city list (e.g. 94608 + a San-Pablo-Ave street -> [Oakland] instead of
// [Emeryville, Oakland]); a street/state that resolves to none falls back to the full ZIP list
// (never empty). SDN relay of these fields is tracked in GIM-5462 — until it forwards them the
// call gracefully returns the full list, so passing them is always safe.
/**
 * Resolve the city/state for a ZIP/postal code.
 * - Legacy (flag off): GET /api/util/countries/{cc}/search?zipCode= → single `{ city, state }`.
 * - AVS Loqate v2 (flag on): GET .../lookupCitiesByPostalCode → `{ isMultiCity, cities[], city, state }`,
 *   where `city`/`state` are the first (preferred) match; optional narrowing inputs (GIM-5588) trim the list.
 * @param {string} countryCode ISO country code (e.g. 'US', 'CA')
 * @param {string} zipCode ZIP/postal code to look up
 * @param {Object} [narrowingParams] optional AVS v2 narrowing inputs (ignored on the legacy path)
 * @param {string} [narrowingParams.address1] street line 1
 * @param {string} [narrowingParams.address2] street line 2 (unit/suite)
 * @param {string} [narrowingParams.state] state/province to constrain the returned cities
 * @returns {Promise<Object>} resolves to the city/state (v2 also: isMultiCity, cities[]); rejects with the raw response on errorCode or no cities
 */
async function getStateAndCityForZipCode(countryCode, zipCode, narrowingParams = {}) {
    const avsLoqateV2 = Sephora.configurationSettings?.avsLoqateV2;

    if (avsLoqateV2) {
        const { address1, address2, state } = narrowingParams;

        let url = '/api/util/countries/' + encodeURIComponent(countryCode) + '/lookupCitiesByPostalCode?zipCode=' + encodeURIComponent(zipCode);

        // GIM-5588: append the optional narrowing inputs only when present so a ZIP-only call stays
        // byte-compatible with the pre-GIM-5588 contract.
        if (address1) {
            url += '&address1=' + encodeURIComponent(address1);
        }

        if (address2) {
            url += '&address2=' + encodeURIComponent(address2);
        }

        if (state) {
            url += '&state=' + encodeURIComponent(state);
        }

        const data = await ufeApi.makeRequest(url, { method: 'GET' });

        // Reject with the raw response object (callers read/log data.errorCode); throw keeps this
        // async/await-consistent and the rejection value identical to the legacy path.
        if (data.errorCode) {
            throw data;
        }

        // Normalize to an array so a malformed non-array `cities` fails closed (rejected below) rather
        // than crashing on cities[0] or returning { city: undefined, state: undefined }.
        const cities = Array.isArray(data.cities) ? data.cities : [];

        if (cities.length === 0) {
            throw data;
        }

        const isMultiCity = cities.length > 1;

        // Always expose a top-level city/state (first match — the service orders the preferred city
        // first). Consumers that render a multi-city dropdown branch on `isMultiCity` and use
        // `cities`; consumers that cannot (ApplePay, BOPIS pickup forms, billing autofill) read
        // `city`/`state` directly and must never receive `undefined`, which would blank/lock fields
        // or corrupt autofill once the flag is on.
        return {
            isMultiCity,
            cities,
            city: cities[0].city,
            state: cities[0].state
        };
    }

    // Legacy path stays byte-for-byte identical to the pre-AVS-v2 request (no encodeURIComponent) so
    // flag-off behavior is unchanged; window.fetch handles any URL encoding, exactly as before.
    const url = '/api/util/countries/' + countryCode + '/search?zipCode=' + zipCode;

    const data = await ufeApi.makeRequest(url, { method: 'GET' });

    if (data.errorCode) {
        throw data;
    }

    return data;
}

export default getStateAndCityForZipCode;
